'use client';

import { useEffect, useRef, useState, type RefObject } from 'react';

/**
 * Measure the host element and return the scale that contains a fixed design canvas
 * inside it. Observing the element (not `window`) keeps it correct in any host: a
 * centered broadcast page, or a fill-the-iframe embed.
 *
 * Scale starts at 0 so the canvas stays hidden until measured, which avoids a
 * one-frame flash of the unscaled (overflowing) full-width layout.
 *
 * Used by the broadcast/TV frames, whose designs are never reflowed: they are laid
 * out once at `stageWidth x stageHeight` and contain-scaled into whatever box the
 * host gives them.
 */
export const useFitScale = (stageWidth: number, stageHeight: number): [RefObject<HTMLDivElement | null>, number] => {
    const ref = useRef<HTMLDivElement>(null);
    const [scale, setScale] = useState(0);

    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        const observer = new ResizeObserver((entries) => {
            const { width, height } = entries[0]!.contentRect;
            const next = Math.min(width / stageWidth, height / stageHeight);
            // Ignore sub-pixel jitter so a stable container can't churn re-renders.
            setScale((prev) => (Math.abs(prev - next) < 0.001 ? prev : next));
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, [stageWidth, stageHeight]);

    return [ref, scale];
};
